import { createSlice, PayloadAction, Selector } from "@reduxjs/toolkit";

import { Paginated } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import { BankReconciliation } from "@/jenesys-types/jenesys-entities/config";

import { RootState } from "@/store/rootReducer";

export interface FetchReconciliationListPayload {
  companyId: string;
  bankInstitutionId?: string;
}

export interface AddBankPayload {
  companyId: string;
  bankData: {
    name: string;
    account_name?: string;
    account_number?: string;
    account_type?: string;
    company_id?: string;
  };
  onSuccess?: () => void;
}

export interface CreateBankAccountPayload {
  companyId: string;
  accountData: {
    bank_connection_id: string;
    name: string;
    status: string;
  };
  onSuccess?: () => void;
}

interface UploadStatementPayload {
  bankAccountId: string;
  files: File[];
  onSuccess?: () => void;
}

interface SyncStatementPayload {
  bankAccountId: string;
  documentId: string;
  reconciliationId: string;
  onSuccess?: () => void;
}

export interface BulkManualMatchPayload {
  companyId: string;
  accountId: string;
  transactionIds: string[];
  manualMatchReason: string;
  onSuccess?: () => void;
}

export interface DeleteBankAccountPayload {
  companyId: string;
  bankAccountId: string;
  onSuccess?: () => void;
}

interface ReconciliationState {
  list: Paginated<BankReconciliation>;
  isLoading: boolean;
  error?: AppError;
  addBankLoading: boolean;
  addBankError?: AppError;
  createBankAccountLoading: boolean;
  createBankAccountError?: AppError;
  deleteBankAccountLoading: boolean;
  deleteBankAccountError?: AppError;
  uploadStatementLoading: boolean;
  uploadStatementError?: AppError;
  uploadStatementSuccess: boolean;
  syncStatementLoading: boolean;
  syncStatementError?: AppError;
  bulkManualMatchLoading: boolean;
  bulkManualMatchError?: AppError;
}

const initialState: ReconciliationState = {
  list: { items: [] },
  isLoading: false,
  error: undefined,
  addBankLoading: false,
  addBankError: undefined,
  createBankAccountLoading: false,
  createBankAccountError: undefined,
  deleteBankAccountLoading: false,
  deleteBankAccountError: undefined,
  uploadStatementLoading: false,
  uploadStatementError: undefined,
  uploadStatementSuccess: false,
  syncStatementLoading: false,
  syncStatementError: undefined,
  bulkManualMatchLoading: false,
  bulkManualMatchError: undefined
};

const reconciliationSlice = createSlice({
  name: "reconciliation",
  initialState,
  reducers: {
    fetchReconciliationList: {
      reducer: (
        state,
        _action: PayloadAction<FetchReconciliationListPayload>
      ) => {
        state.isLoading = true;
        state.error = undefined;
      },
      prepare: (payload: FetchReconciliationListPayload) => ({
        payload
      })
    },
    fetchReconciliationListSuccess: {
      reducer: (
        state,
        action: PayloadAction<Paginated<BankReconciliation>>
      ) => {
        state.isLoading = false;
        state.list = action.payload;
      },
      prepare: (list: Paginated<BankReconciliation>) => ({
        payload: list
      })
    },
    fetchReconciliationListFailure: {
      reducer: (state, action: PayloadAction<AppError>) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    addBank: {
      reducer: (state, _action: PayloadAction<AddBankPayload>) => {
        state.addBankLoading = true;
        state.addBankError = undefined;
      },
      prepare: (payload: AddBankPayload) => ({
        payload
      })
    },
    addBankSuccess: {
      reducer: (state) => {
        state.addBankLoading = false;
      },
      prepare: () => ({ payload: undefined })
    },
    addBankFailure: {
      reducer: (state, action: PayloadAction<AppError>) => {
        state.addBankLoading = false;
        state.addBankError = action.payload;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    createBankAccount: {
      reducer: (state, _action: PayloadAction<CreateBankAccountPayload>) => {
        state.createBankAccountLoading = true;
        state.createBankAccountError = undefined;
      },
      prepare: (payload: CreateBankAccountPayload) => ({
        payload
      })
    },
    createBankAccountSuccess: {
      reducer: (state) => {
        state.createBankAccountLoading = false;
      },
      prepare: () => ({ payload: undefined })
    },
    createBankAccountFailure: {
      reducer: (state, action: PayloadAction<AppError>) => {
        state.createBankAccountLoading = false;
        state.createBankAccountError = action.payload;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    deleteBankAccount: {
      reducer: (state, _action: PayloadAction<DeleteBankAccountPayload>) => {
        state.deleteBankAccountLoading = true;
        state.deleteBankAccountError = undefined;
      },
      prepare: (payload: DeleteBankAccountPayload) => ({ payload })
    },
    deleteBankAccountSuccess: {
      reducer: (state) => {
        state.deleteBankAccountLoading = false;
      },
      prepare: () => ({ payload: undefined })
    },
    deleteBankAccountFailure: {
      reducer: (state, action: PayloadAction<AppError>) => {
        state.deleteBankAccountLoading = false;
        state.deleteBankAccountError = action.payload;
      },
      prepare: (error: AppError) => ({ payload: error })
    },
    uploadStatement: {
      reducer: (state, _action: PayloadAction<UploadStatementPayload>) => {
        state.uploadStatementLoading = true;
        state.uploadStatementError = undefined;
        state.uploadStatementSuccess = false;
      },
      prepare: (payload: UploadStatementPayload) => ({
        payload
      })
    },
    uploadStatementSuccess: {
      reducer: (state) => {
        state.uploadStatementLoading = false;
        state.uploadStatementSuccess = true;
      },
      prepare: () => ({ payload: undefined })
    },
    uploadStatementFailure: {
      reducer: (state, action: PayloadAction<AppError>) => {
        state.uploadStatementLoading = false;
        state.uploadStatementError = action.payload;
        state.uploadStatementSuccess = false;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    syncStatement: {
      reducer: (state, _action: PayloadAction<SyncStatementPayload>) => {
        state.syncStatementLoading = true;
        state.syncStatementError = undefined;
      },
      prepare: (payload: SyncStatementPayload) => ({ payload })
    },
    syncStatementSuccess: {
      reducer: (state) => {
        state.syncStatementLoading = false;
      },
      prepare: () => ({ payload: undefined })
    },
    syncStatementFailure: {
      reducer: (state, action: PayloadAction<AppError>) => {
        state.syncStatementLoading = false;
        state.syncStatementError = action.payload;
      },
      prepare: (error: AppError) => ({ payload: error })
    },
    bulkManualMatch: {
      reducer: (state, _action: PayloadAction<BulkManualMatchPayload>) => {
        state.bulkManualMatchLoading = true;
        state.bulkManualMatchError = undefined;
      },
      prepare: (payload: BulkManualMatchPayload) => ({ payload })
    },
    bulkManualMatchSuccess: {
      reducer: (state) => {
        state.bulkManualMatchLoading = false;
      },
      prepare: () => ({ payload: undefined })
    },
    bulkManualMatchFailure: {
      reducer: (state, action: PayloadAction<AppError>) => {
        state.bulkManualMatchLoading = false;
        state.bulkManualMatchError = action.payload;
      },
      prepare: (error: AppError) => ({ payload: error })
    }
  }
});

export const {
  fetchReconciliationList,
  fetchReconciliationListSuccess,
  fetchReconciliationListFailure,
  addBank,
  addBankSuccess,
  addBankFailure,
  createBankAccount,
  createBankAccountSuccess,
  createBankAccountFailure,
  deleteBankAccount,
  deleteBankAccountSuccess,
  deleteBankAccountFailure,
  uploadStatement,
  uploadStatementSuccess,
  uploadStatementFailure,
  syncStatement,
  syncStatementSuccess,
  syncStatementFailure,
  bulkManualMatch,
  bulkManualMatchSuccess,
  bulkManualMatchFailure
} = reconciliationSlice.actions;

export default reconciliationSlice.reducer;

export const selectReconciliation: Selector<RootState, ReconciliationState> = (
  state: RootState
) => state.features.Reconciliation;
